/**
 * Leitura do cron padrão de cinco campos: `minuto hora dia-do-mês mês dia-da-semana`.
 *
 * É a leitura que o engine aplica depois de traduzir para o APScheduler: dia da
 * semana 0 ou 7 = domingo, nomes `sun..sat` e `jan..dec`, `L` = último dia do mês
 * (sozinho no campo). Quando dia do mês e dia da semana estão os dois restritos,
 * vale qualquer um deles.
 */
const FIELDS = ['minute', 'hour', 'day', 'month', 'weekday'];
const LIMITS = {
    minute: [0, 59],
    hour: [0, 23],
    day: [1, 31],
    month: [1, 12],
    weekday: [0, 7],
};
const MONTH_NAMES = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];
const WEEKDAY_NAMES = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
/** Espaços extras fora; o resto como veio. */
export function normalizeCron(cron) {
    return cron.trim().split(/\s+/).filter(Boolean).join(' ');
}
export function validateCron(cron) {
    const parts = normalizeCron(cron).split(' ').filter(Boolean);
    if (parts.length !== 5) {
        return {
            ok: false,
            field: null,
            message: 'Use five fields: minute hour day-of-month month day-of-week.',
        };
    }
    const fields = {};
    let lastDay = false;
    for (const [index, name] of FIELDS.entries()) {
        const token = parts[index] ?? '';
        if (name === 'day' && token.toUpperCase() === 'L') {
            lastDay = true;
            fields.day = { token: 'L', any: false, values: [] };
            continue;
        }
        const values = parseField(token, name);
        if (!values) {
            return { ok: false, field: name, message: `Invalid ${FIELD_LABEL[name]}: "${token}".` };
        }
        fields[name] = { token, any: token === '*', values };
    }
    return {
        ok: true,
        cron: {
            minute: fields.minute,
            hour: fields.hour,
            day: { ...fields.day, lastDay },
            month: fields.month,
            weekday: fields.weekday,
        },
    };
}
/** O cron lido, ou null quando ele não é válido. */
export function parseCron(cron) {
    const result = validateCron(cron);
    return result.ok ? result.cron : null;
}
const FIELD_LABEL = {
    minute: 'minute',
    hour: 'hour',
    day: 'day of month',
    month: 'month',
    weekday: 'day of week',
};
function parseField(token, name) {
    const [min, max] = LIMITS[name];
    const values = new Set();
    for (const part of token.split(',')) {
        if (!part)
            return null;
        const [base = '', stepText, extra] = part.split('/');
        if (extra !== undefined)
            return null;
        const step = stepText === undefined ? 1 : integer(stepText);
        if (step === null || step < 1)
            return null;
        let start;
        let end;
        if (base === '*') {
            start = min;
            end = name === 'weekday' ? 6 : max;
        }
        else if (base.includes('-')) {
            const [from = '', to = '', more] = base.split('-');
            if (more !== undefined)
                return null;
            start = value(from, name);
            end = value(to, name);
        }
        else {
            start = value(base, name);
            // `5/15` = do 5 em diante, de 15 em 15.
            end = stepText === undefined ? start : name === 'weekday' ? 6 : max;
        }
        if (start === null || end === null || start < min || end > max || start > end)
            return null;
        for (let v = start; v <= end; v += step)
            values.add(name === 'weekday' ? v % 7 : v);
    }
    return [...values].sort((a, b) => a - b);
}
function value(text, name) {
    const lower = text.toLowerCase();
    if (name === 'month') {
        const index = MONTH_NAMES.indexOf(lower);
        if (index >= 0)
            return index + 1;
    }
    if (name === 'weekday') {
        const index = WEEKDAY_NAMES.indexOf(lower);
        if (index >= 0)
            return index;
    }
    return integer(text);
}
function integer(text) {
    return /^\d+$/.test(text) ? Number(text) : null;
}
//# sourceMappingURL=cron.js.map